<template>
	<div class="contain" style="max-width:1242px">
		<div :class="canSwitchQueueType ? 'mb-20' : 'mb-32'" class="flex-split align-top gap-24 wrap mb-20">
			<h1 class="fs-32 lh-28 fw-800 my-0">Build {{ queue.type == 'ranked' ? 'Ranked' : 'Instant' }} Offer Queue</h1>
			<button v-if="canSwitchQueueType" class="btn btn-large gap-8" @click="switchQueueType">
				<svg v-if="queue.type == 'instant'" class="block" xmlns="http://www.w3.org/2000/svg" width="18" height="17" fill="none" viewBox="0 0 18 17">
					<path stroke="currentColor" stroke-width="1.5" d="M5.41614 10.0178L4.93678 10.5947L5.41613 10.0178L1.92065 7.11304L6.47673 6.8027C6.83939 6.778 7.15616 6.54852 7.29265 6.21161L9 1.99738L10.7073 6.21161C10.8438 6.54851 11.1606 6.778 11.5233 6.8027L16.0764 7.11284L12.5737 10.0172C12.2913 10.2512 12.169 10.6269 12.2593 10.9824L12.9862 10.7977L12.2593 10.9824L13.3754 15.3761L9.50278 12.9605C9.19506 12.7686 8.80493 12.7686 8.49723 12.9605L8.89415 13.5969L8.49723 12.9605L4.62075 15.3784L5.7301 10.9808C5.81957 10.6262 5.69746 10.2516 5.41614 10.0178Z" />
				</svg>
				<svg v-else class="block" xmlns="http://www.w3.org/2000/svg" width="12" height="17" fill="none" viewBox="0 0 12 17">
					<path fill="#fff" stroke="currentColor" stroke-width="1.5" d="M6.6686 0.785333L6.66869 0.785195C6.66874 0.785227 6.67871 0.771826 6.70868 0.760858C6.74078 0.749115 6.78058 0.746337 6.81769 0.755281C6.84403 0.761629 6.86041 0.771606 6.86898 0.778372V6.03647V6.78647H7.61898H11.0051H11.0053C11.0357 6.78646 11.0621 6.79334 11.0815 6.80249L5.18325 15.8147L5.18316 15.8148C5.18312 15.8148 5.17314 15.8282 5.14317 15.8391L5.38924 16.5117L5.14316 15.8392C5.11107 15.8509 5.07128 15.8537 5.03416 15.8447C5.00782 15.8384 4.99145 15.8284 4.98288 15.8216V10.5635V9.81354H4.23288H0.846776H0.846579C0.816142 9.81354 0.789741 9.80667 0.770381 9.79751L6.6686 0.785333ZM11.1054 6.76593L11.1054 6.76595L11.4293 6.13791L11.1054 6.76593ZM0.746459 9.83407L0.422578 10.4621L0.746437 9.8341L0.746459 9.83407Z" />
				</svg>
				Switch to {{ queue.type == 'ranked' ? 'Instant' : 'Ranked' }} Offer
			</button>
		</div>
		<div class="flex-col gap-24 lg-flex-row lg-align-start">
			<main class="lg-flex-66">
				<div class="card-v2">
					<EditJobQueueForm
						v-if="queue"
						v-model="queue"
						:can-remove-candidates="true"
						:confirm-discard="false"
						:copy-model="false"
						:disabled="isLoading"
						:id="queueFormId"
						:show-find-more="false"
						:verb="null"
						@remove="onRemove"
						@save="onQueueSaved"
						@disable-submit="disableSubmit = true"
						@enable-submit="disableSubmit = false"
					/>
					<p v-else-if="isLoading">Loading...</p>
					<p v-else>This job is unavailable or may have been deleted.</p>
					<div class="flex-split gap-16 mt-32">
						<button class="btn" @click="back">Back</button>
						<SubmitButton class="btn-green btn-large" :disabled="isLoading || disableSubmit" :form="queueFormId">
							{{ isExistingQueue && queue.type == 'instant' ? 'Send Instant Offer' : 'Start Job Offer Queue' }}
						</SubmitButton>
					</div>
				</div>
			</main>
			<aside class="lg-flex-34">
				<div class="card-v2">
					<h3 class="text-center mw-230 mx-auto">How does {{ queue.type == 'ranked' ? 'Ranked' : 'Instant' }} Offer Queue Work?</h3>
					<RankedOfferExplainer v-if="queue.type == 'ranked'" />
					<InstantOfferExplainer v-else />	
				</div>
			</aside>
		</div>

		<QueueNotStartedModal v-model="pendingNavigation" />
	</div>
</template>

<script>
import EditJobQueueForm from '@/components/forms/EditJobQueueForm';
import QueueNotStartedModal from '@/components/jobs/QueueNotStartedModal';
import RankedOfferExplainer from '@/components/jobs/RankedOfferExplainer';
import InstantOfferExplainer from '@/components/jobs/InstantOfferExplainer';
import Role from '@/models/Role';

export default {
	name: 'FindHelpQueue',
	components: {
		EditJobQueueForm,
		QueueNotStartedModal,
		RankedOfferExplainer,
		InstantOfferExplainer,
	},
	props: {
		eventId: {
			type: String,
			required: true,
		},
	},
	data() {
		return {
			disableSubmit: false,
			collapseSidebar: false,
			isDone: false,
			isLoading: false,
			pendingNavigation: null,
		};
	},
	computed: {
		isExistingQueue() {
			return !!this.queue?.id;
		},
		canSwitchQueueType() {
			return this.queue.status !== 'running';
		},
		event: {
			get() {
				return this.$store.getters['events/searchEvent'];
			},
			set(event) {
				this.$store.commit('events/setSearchEvent', event);
			},
		},
		futureCandidates() {
			const { queue } = this;
			if (!queue) {
				return [];
			}

			const { candidates } = queue;
			if (!queue.id) {
				// Queue has no ID, which means it hasn't started yet. So all of the candidates are
				// considered future candidates, because none of them have been offered the job yet
				return candidates;
			}
			const { currentIndex } = queue;
			const futureCandidates = [];

			for (const candidate of candidates) {
				if ( (currentIndex == null || candidate.order > currentIndex) && candidate.status === 'inQueue' && candidate.userId) {
					futureCandidates.push(candidate);
				}
			}
			return futureCandidates;
		},
		job() {
			const jobs = this.jobs;
			return (jobs.length && jobs[0]) || null;
		},
		jobs() {
			const event = this.event;
			let jobs;
			
			if (event && this.$store.getters['roles/list']) {
				const studioRoleId = Role.studio.id;
				jobs = event.jobs.filter((job) => job.positionId !== studioRoleId);
			} else {
				jobs = [];
			}
			return Object.freeze(jobs);
		},
		queue: {
			get() {
				return this.$store.getters['events/searchQueue'];
			},
			set(queue) {
				this.$store.commit('events/setSearchQueue', queue);
			},
		},
		queueFormId() {
			return 'queue-form';
		},
	},
	methods: {
		switchQueueType() {
			const newType = this.queue.type == 'ranked' ? 'instant' : 'ranked';
			this.queue.type = newType;
		},
		back() {
			this.goToSearchPage();
		},
		async fetchData() {
			const { eventId } = this;
			if (!eventId) {
				this.event = null;
				this.queue = null;
				return;
			}

			let { job, event, queue } = this;
			try {
				if (!event || event.id !== eventId || !job) {
					// get the event
					this.event = null;
					this.isLoading = true;
					event = this.event = await this.$store.dispatch('events/getEvent', eventId);
					if (!event || !(job = this.job)) {
						this.event = null;
						return;
					}
				}

				const jobId = job.id;
				if (jobId && (!queue || queue.eventId !== eventId || queue.jobId !== jobId)) {
					// get the queue or create a new one
					this.isLoading = true;
					try {
						queue = await this.$store.dispatch('jobs/getJobQueue', jobId);
					} catch (e) {
						queue = null;
					}
					this.queue = queue;
				}
			} finally {
				this.isLoading = false;
				this.goToSearchPageIfNoFutureCandidates();
			}
		},
		goToSearchPage(replace) {
			this.$router[replace ? 'replace' : 'push']({
				name: this.$routes.FindHelpSearch.name,
				params: {
					eventId: this.eventId
				},
			});
		},
		goToSearchPageIfNoFutureCandidates() {
			if (!this.futureCandidates?.length) {
				return this.goToSearchPage(true);
			}
		},
		async onQueueSaved() {
			const { queue, $store: store } = this;

			let jobId;
			if (!queue || !(jobId = queue.jobId)) {
				return;
			}

			this.isLoading = true;
			await store.dispatch('jobs/loadJobView', {
				force: true,
				jobId
			});
			const job = store.getters['jobs/job'];

			if (!job || !(jobId = job.id)) {
				this.event = null;
				this.isLoading = false;
				this.queue = null;
				return;
			}

			const oldQueue = store.getters['jobs/queue'];
			const fallbackTime = Number.NEGATIVE_INFINITY;
			if (
				!oldQueue ||
				(oldQueue.lastProcessedAt ?? fallbackTime) < (queue.lastProcessedAt ?? fallbackTime) ||
				(oldQueue.updatedAt ?? fallbackTime) < (queue.updatedAt ?? fallbackTime)
			) {
				store.commit('jobs/setQueue', queue);
			}

			this.isDone = true;
			// Redirect to the event details page
			await this.$router.push({
				name: this.$routes.JobDetails.name,
				params: {
					jobId
				}
			});

			// Clear the JobEvent and JobQueue from state because we're done
			store.commit('events/setSearchEvent', null);
			store.commit('events/setSearchQueue', null);
			store.commit('users/setHasCreatedOrRecievedJob', true);
		},
		onRemove() {
			this.goToSearchPageIfNoFutureCandidates();
		},
	},
	beforeRouteLeave(to, from, next) {
		let eventId;

		if (!this.isDone && (eventId = this.eventId) && (to.name !== 'findHelpSearch' || to.params?.eventId !== eventId)) {
			this.pendingNavigation = next;
		} else {
			next();
		}
	},
	watch: {
		eventId: {
			handler() {
				this.fetchData();
			},
			immediate: true,
		},
	},
	metaInfo: {
		title: 'Finalize Job Offer',
	},
};
</script>
